import { CreateAnalyticsProductDto } from "@intelligems/ig-types";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { toGqlId } from "@src/api/formatters/shopify";
import {
  IAnalyticsProductsState,
  sliceInitialAnalyticsProductsState,
} from "@src/store/types/analyticsProducts";

const analyticsProducts = createSlice({
  name: "analyticsProducts",
  initialState: sliceInitialAnalyticsProductsState,
  reducers: {
    resetAnalyticsProducts: (draft) => {
      const storedProducts = draft.storedAnalyticsProducts;
      Object.assign(draft, {
        ...sliceInitialAnalyticsProductsState,
        isSuccess: true,
        storedAnalyticsProducts: storedProducts,
      });
    },
    initAnalyticsProducts: (
      draft,
      action: PayloadAction<
        Pick<
          IAnalyticsProductsState,
          "isFetching" | "isLoading" | "isSuccess" | "analyticsProducts"
        >
      >
    ) => {
      const { isFetching, isLoading, isSuccess, analyticsProducts } =
        action.payload;

      draft.isFetching = isFetching;
      draft.isLoading = isLoading;
      draft.isSuccess = isSuccess;
      draft.analyticsProducts = analyticsProducts;
      draft.storedAnalyticsProducts = analyticsProducts;
    },
    removeAnalyticsProducts: (
      draft,
      action: PayloadAction<{ productIds: string[] }>
    ) => {
      if (!draft.isSuccess) return;

      draft.analyticsProducts = draft.analyticsProducts.filter(
        (testProd) =>
          !action.payload.productIds.some(
            (id) => toGqlId(id, "Product") === toGqlId(testProd.id, "Product")
          )
      );
    },
    setAnalyticsProducts: (
      draft,
      action: PayloadAction<CreateAnalyticsProductDto[]>
    ) => {
      draft.analyticsProducts = action.payload;
    },
    resetAnalyticsProductsToStored: (draft) => {
      if (!draft.isSuccess) return;

      draft.analyticsProducts = draft.storedAnalyticsProducts;
    },
    processSavedAnalyticsProducts: (draft) => {
      draft.productsLocation = undefined;
      draft.storedAnalyticsProducts = draft.analyticsProducts;
    },
  },
  selectors: {
    getAnalyticsProducts: (state) => state.analyticsProducts,
    getAnalyticsProductCount: (state) => state.analyticsProducts.length,
    selectedAnalyticsProductsState: (state) => state,
    areAnalyticsProductsLoaded: (state) => state.isSuccess,
    areAnalyticsProductsLoading: (state) => state.isLoading,
    areAnalyticsProductsFetching: (state) => state.isFetching,
    getAnalyticsProductsLocation: (state) => state.productsLocation,
  },
});

export const {
  removeAnalyticsProducts,
  initAnalyticsProducts,
  resetAnalyticsProducts,
  setAnalyticsProducts,
  resetAnalyticsProductsToStored,
  processSavedAnalyticsProducts,
} = analyticsProducts.actions;

export const analyticsProductsReducer = analyticsProducts.reducer;
export const {
  getAnalyticsProducts,
  getAnalyticsProductCount,
  selectedAnalyticsProductsState,
  areAnalyticsProductsLoaded,
  areAnalyticsProductsLoading,
  areAnalyticsProductsFetching,
  getAnalyticsProductsLocation,
} = analyticsProducts.selectors;
